<template>
  <el-card shadow="hover" class="chart-card">
    <div slot="header" class="card-header">
      <span>{{ title || '客户揽收情况观察 (Observação da situação de coleta dos clientes)' }}</span>
      <img :src="exportIcon" class="export-btn" alt="导出" @click="handleExport" />
    </div>
    <base-echart :options="chartOption" height="682px" />
  </el-card>
</template>

<script>
import * as echarts from 'echarts'
import BaseEchart from './BaseEchart'

/**
 * 客户揽收情况观察图表组件
 * 展示客户的应揽收票数、实际揽收票数和揽收差异
 * 包含柱状图（按客户分组）和趋势图（按日期）
 */
export default {
  name: 'CustomerCollectionChart',
  components: { BaseEchart },
  props: {
    title: {
      type: String,
      default: '',
      description: '图表标题'
    },
    customerData: {
      type: Array,
      default: () => [],
      description: '按客户分组的揽收数据'
    },
    dailyData: {
      type: Array,
      default: () => [],
      description: '按日期分组的揽收趋势数据'
    }
  },
  data() {
    return {
      chartOption: {}, // ECharts配置对象
      exportIcon: require('@/assets/images/kanban/endKanbanBoard/Export.svg')
    }
  },
  watch: {
    customerData: {
      handler() {
        this.updateChartData()
      },
      deep: true
    },
    dailyData: {
      handler() {
        this.updateChartData()
      },
      deep: true
    }
  },
  created() {
    // 组件创建时初始化图表配置
    this.initChartOption()
  },
  methods: {
    /**
     * 初始化图表配置
     * 配置双层图表：上层为柱状图，下层为面积趋势图
     */
    initChartOption() {
      // 定义图表配色方案
      const colorGreen = '#2AAB80' // 绿色 - 应揽收票数
      const colorBlue = '#4581DE' // 蓝色 - 实际揽收票数
      const colorOrange = '#F97216' // 橙色 - 揽收差异
      const colors = [colorGreen, colorBlue, colorOrange]

      this.chartOption = {
        color: colors, // 使用配色方案
        tooltip: {
          trigger: 'axis', // 坐标轴触发提示
          axisPointer: { type: 'cross' } // 十字准星指示器
        },
        legend: [
          // 上层柱状图图例
          {
            data: [
              '应揽收票数 Qtd. de votos a serem coletados',
              '实际揽收票数 Qtd. de votos a real coletados',
              '揽收差异 Diferença na qtd. de coletas'
            ],
            top: '3%',
            icon: 'circle'
          },
          // 下层趋势图图例
          {
            data: [
              '应揽收票数 Qtd. de votos a serem coletados',
              '实际揽收票数 Qtd. de votos a real coletados',
              '揽收差异 Diferença na qtd. de coletas'
            ],
            top: '58%',
            icon: 'circle'
          }
        ],
        grid: [
          { top: '15%', height: '30%', left: '8%', right: '5%', bottom: '53%' }, // 上层柱状图网格
          { top: '65%', height: '28%', left: '8%', right: '5%', bottom: '5%' } // 下层趋势图网格
        ],
        dataZoom: [
          // 上层柱状图（按客户）的横向滚动条
          {
            type: 'slider',
            show: false, // 初始不显示，在updateChartData中根据数据量决定
            xAxisIndex: [0],
            start: 0,
            end: 100, // 初始值100%，将在updateChartData中根据实际数据量更新
            height: 20,
            top: '50%',
            borderColor: '#ccc',
            fillerColor: 'rgba(42, 171, 128, 0.2)',
            handleStyle: { color: '#2AAB80' }
          },
          // 下层趋势图（按日期）的横向滚动条
          {
            type: 'slider',
            show: false, // 初始不显示，在updateChartData中根据数据量决定
            xAxisIndex: [1],
            start: 0,
            end: 100, // 初始值100%，将在updateChartData中根据实际数据量更新
            height: 20,
            bottom: 5,
            borderColor: '#ccc',
            fillerColor: 'rgba(69, 129, 222, 0.2)',
            handleStyle: { color: '#4581DE' }
          }
        ],
        xAxis: [
          {
            type: 'category',
            data: [],
            gridIndex: 0,
            axisTick: { show: false },
            axisLine: { lineStyle: { color: '#D9D9D9', type: 'dashed' } },
            axisLabel: { margin: 20, color: '#000' }
          },
          {
            type: 'category',
            data: [],
            gridIndex: 1,
            boundaryGap: false,
            axisLine: { lineStyle: { color: '#D9D9D9', type: 'dashed' } },
            axisTick: { show: false },
            axisLabel: { color: '#666' }
          }
        ],
        yAxis: [
          {
            type: 'value',
            gridIndex: 0,
            splitLine: { show: true, lineStyle: { type: 'dashed', color: '#E0E6F1' } }
          },
          {
            type: 'value',
            gridIndex: 1,
            splitLine: { show: true, lineStyle: { type: 'dashed', color: '#E0E6F1' } }
          }
        ],
        series: [
          // ===== 上层：按客户的柱状图 =====
          {
            name: '应揽收票数 Qtd. de votos a serem coletados',
            type: 'bar',
            data: [],
            xAxisIndex: 0,
            yAxisIndex: 0,
            barWidth: 10,
            itemStyle: {
              borderRadius: [5, 5, 0, 0], // 默认顶部圆角，会在updateChartData中根据数据动态设置
              shadowColor: 'rgba(42,171,128,0.8)',
              shadowBlur: 6,
              shadowOffsetY: 3 // 阴影效果
            }
          },
          {
            name: '实际揽收票数 Qtd. de votos a real coletados',
            type: 'bar',
            data: [],
            xAxisIndex: 0,
            yAxisIndex: 0,
            barWidth: 10,
            itemStyle: {
              borderRadius: [5, 5, 0, 0], // 默认顶部圆角，会在updateChartData中根据数据动态设置
              shadowColor: 'rgba(69, 129, 222, 0.8)',
              shadowBlur: 6,
              shadowOffsetY: 3
            }
          },
          {
            name: '揽收差异 Diferença na qtd. de coletas',
            type: 'bar',
            data: [],
            xAxisIndex: 0,
            yAxisIndex: 0,
            barWidth: 10,
            itemStyle: {
              borderRadius: [5, 5, 0, 0], // 默认顶部圆角，会在updateChartData中根据数据动态设置
              shadowColor: 'rgba(249, 114, 22, 0.8)',
              shadowBlur: 6,
              shadowOffsetY: 3
            }
          },
          // ===== 下层：按日期的面积趋势图 =====
          {
            name: '应揽收票数 Qtd. de votos a serem coletados',
            type: 'line',
            data: [],
            xAxisIndex: 1,
            yAxisIndex: 1,
            smooth: true,
            showSymbol: false, // 平滑曲线，不显示数据点
            areaStyle: {
              opacity: 0.5,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                // 渐变填充
                { offset: 0, color: 'rgba(42, 171, 128, 0.5)' },
                { offset: 1, color: 'rgba(255, 255, 255, 0)' }
              ])
            }
          },
          {
            name: '实际揽收票数 Qtd. de votos a real coletados',
            type: 'line',
            data: [],
            xAxisIndex: 1,
            yAxisIndex: 1,
            smooth: true,
            showSymbol: false,
            areaStyle: {
              opacity: 0.8,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#C0D7FB' },
                { offset: 1, color: 'rgba(255, 255, 255, 0)' }
              ])
            }
          },
          {
            name: '揽收差异 Diferença na qtd. de coletas',
            type: 'line',
            data: [],
            xAxisIndex: 1,
            yAxisIndex: 1,
            smooth: true,
            showSymbol: false,
            areaStyle: {
              opacity: 0.5,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(249, 114, 22, 0.5)' },
                { offset: 1, color: 'rgba(255, 255, 255, 0)' }
              ])
            }
          }
        ]
      }
    },

    /**
     * 更新图表数据
     * 根据props中的真实数据更新图表
     */
    updateChartData() {
      // 当数据为空时，显式清空图表数据
      if (!this.customerData.length && !this.dailyData.length) {
        // 清空上层柱状图数据
        this.chartOption.xAxis[0].data = []
        this.chartOption.series[0].data = []
        this.chartOption.series[1].data = []
        this.chartOption.series[2].data = []
        this.chartOption.dataZoom[0].show = false
        this.chartOption.grid[0].bottom = '53%'

        // 清空下层趋势图数据
        this.chartOption.xAxis[1].data = []
        this.chartOption.series[3].data = []
        this.chartOption.series[4].data = []
        this.chartOption.series[5].data = []
        this.chartOption.dataZoom[1].show = false
        this.chartOption.grid[1].bottom = '5%'

        // 触发图表更新
        this.chartOption = { ...this.chartOption }
        return
      }

      const defaultShowCount = 5 // 默认显示5条数据
      const showScrollBarThreshold = 6 // 超过6条才显示滚动条

      // 更新上层柱状图数据（按客户）
      if (this.customerData.length > 0) {
        const customerNames = this.customerData.map((item) => item.customerName)

        // 为每个数据点设置独立的样式，根据正负值设置不同的圆角
        const exceptCollectData = this.customerData.map((item) => ({
          value: item.exceptCollectCnt,
          itemStyle: {
            borderRadius: item.exceptCollectCnt >= 0 ? [5, 5, 0, 0] : [0, 0, 5, 5]
          }
        }))
        const actualCollectData = this.customerData.map((item) => ({
          value: item.actualCollectCnt,
          itemStyle: {
            borderRadius: item.actualCollectCnt >= 0 ? [5, 5, 0, 0] : [0, 0, 5, 5]
          }
        }))
        const collectDiffData = this.customerData.map((item) => ({
          value: item.collectDiffCnt,
          itemStyle: {
            borderRadius: item.collectDiffCnt >= 0 ? [5, 5, 0, 0] : [0, 0, 5, 5]
          }
        }))

        this.chartOption.xAxis[0].data = customerNames
        this.chartOption.series[0].data = exceptCollectData // 应揽收票数
        this.chartOption.series[1].data = actualCollectData // 实际揽收票数
        this.chartOption.series[2].data = collectDiffData // 揽收差异

        // 判断是否显示滚动条（超过6条才显示）
        const showCustomerScrollBar = this.customerData.length > showScrollBarThreshold
        this.chartOption.dataZoom[0].show = showCustomerScrollBar

        // 计算并更新上层图表的dataZoom end值
        // 6条及以下全部显示，超过6条才显示前5条
        const customerEndPercent =
          this.customerData.length <= showScrollBarThreshold
            ? 100
            : Math.min(100, (defaultShowCount / this.customerData.length) * 100)
        this.chartOption.dataZoom[0].end = customerEndPercent

        // 动态调整上层grid的bottom值，为滚动条留出足够空间
        this.chartOption.grid[0].bottom = showCustomerScrollBar ? '50%' : '53%'
      } else {
        // 如果没有客户数据，清空上层图表
        this.chartOption.xAxis[0].data = []
        this.chartOption.series[0].data = []
        this.chartOption.series[1].data = []
        this.chartOption.series[2].data = []
        this.chartOption.dataZoom[0].show = false
        this.chartOption.grid[0].bottom = '53%'
      }

      // 更新下层趋势图数据（按日期）
      if (this.dailyData.length > 0) {
        // 按日期排序
        const sortedData = [...this.dailyData].sort((a, b) => new Date(a.dt) - new Date(b.dt))

        const dates = sortedData.map((item) => item.dt)
        const exceptCollectData = sortedData.map((item) => item.exceptCollectCnt)
        const actualCollectData = sortedData.map((item) => item.actualCollectCnt)
        const collectDiffData = sortedData.map((item) => item.collectDiffCnt)

        this.chartOption.xAxis[1].data = dates
        this.chartOption.series[3].data = exceptCollectData // 应揽收票数趋势
        this.chartOption.series[4].data = actualCollectData // 实际揽收票数趋势
        this.chartOption.series[5].data = collectDiffData // 揽收差异趋势

        // 判断是否显示滚动条（超过6条才显示）
        const showDailyScrollBar = this.dailyData.length > showScrollBarThreshold
        this.chartOption.dataZoom[1].show = showDailyScrollBar

        // 计算并更新下层图表的dataZoom end值
        // 6条及以下全部显示，超过6条才显示前5条
        const dailyEndPercent =
          this.dailyData.length <= showScrollBarThreshold
            ? 100
            : Math.min(100, (defaultShowCount / this.dailyData.length) * 100)
        this.chartOption.dataZoom[1].end = dailyEndPercent

        // 动态调整下层grid的bottom值
        this.chartOption.grid[1].bottom = showDailyScrollBar ? '10%' : '5%'
      } else {
        // 如果没有日期数据，清空下层图表
        this.chartOption.xAxis[1].data = []
        this.chartOption.series[3].data = []
        this.chartOption.series[4].data = []
        this.chartOption.series[5].data = []
        this.chartOption.dataZoom[1].show = false
        this.chartOption.grid[1].bottom = '5%'
      }

      // 触发图表更新
      this.chartOption = { ...this.chartOption }
    },

    /**
     * 处理导出
     */
    handleExport() {
      this.$emit('export')
    }
  }
}
</script>

<style scoped lang="scss">
.chart-card {
  margin-bottom: 0;
  height: 100%;
  background: #ffffff;
  border-radius: 12px;
  border: none;

  ::v-deep .el-card__header {
    border-bottom: none;
    padding-bottom: 0;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: Alibaba PuHuiTi 2, Alibaba PuHuiTi 20;
    font-weight: normal;
    font-size: 16px;
    color: #0f1728;
    font-style: normal;
    text-transform: none;
    font-weight: bold;
    .export-btn {
      width: 16px;
      height: 16px;
      cursor: pointer;
      transition: opacity 0.3s;

      &:hover {
        opacity: 0.7;
      }
    }
  }
}
</style>
